/** Ringback while the call connects: one tone, then a pause, like a phone line. */
const RINGBACK_HZ = 425;
const TONE_MS = 1_000;
const PAUSE_MS = 4_000;
const TONE_GAIN = 0.08;
/** Even an instant pickup rings once, so the call reads as a call and not a glitch. */
const MIN_RING_MS = 2_000;

/** Synthesized telephone ringback before realtime capture starts; no microphone or network. */
export function playCallPrelude(context: AudioContext, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const oscillators: OscillatorNode[] = [];
    let gain: GainNode | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      signal.removeEventListener("abort", finish);
      for (const oscillator of oscillators) {
        try {
          oscillator.stop();
        } catch {
          // The audio-clock deadline may already have stopped it.
        }
        oscillator.disconnect();
      }
      gain?.disconnect();
      resolve();
    };
    signal.addEventListener("abort", finish, { once: true });
    try {
      gain = context.createGain();
      gain.connect(context.destination);
      const start = context.currentTime;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.035, start + 0.02);
      gain.gain.setValueAtTime(0.035, start + 1.8);
      gain.gain.linearRampToValueAtTime(0, start + 1.9);
      // A familiar dual-tone outgoing-call cue, synthesized rather than an Apple recording.
      for (const frequency of [440, 480]) {
        const oscillator = context.createOscillator();
        oscillators.push(oscillator);
        oscillator.frequency.value = frequency;
        oscillator.connect(gain);
        oscillator.start(start);
        oscillator.stop(start + 1.9);
      }
      timer = setTimeout(finish, MIN_RING_MS);
    } catch {
      // The optional cue must not prevent the call when audio synthesis is unavailable.
      finish();
    }
  });
}

/**
 * Rings until the call can talk: at least one tone, and until the browser has
 * granted the microphone, so its permission prompt shows during the ringing
 * instead of interrupting the first turn. A refused or missing microphone still
 * picks up; listening reports the error on the card.
 */
export async function pickUp(signal: AbortSignal, options: { ring?: boolean } = {}): Promise<void> {
  if (signal.aborted) return;
  // An incoming call the user just answered has nothing to ring for: only the microphone.
  if (options.ring === false) {
    await microphoneReady();
    return;
  }
  const tone = startRingback();
  signal.addEventListener("abort", tone.stop, { once: true });
  if (signal.aborted) tone.stop();
  try {
    await Promise.all([delay(MIN_RING_MS, signal), microphoneReady()]);
  } finally {
    signal.removeEventListener("abort", tone.stop);
    tone.stop();
  }
}

async function microphoneReady(): Promise<void> {
  const media = globalThis.navigator?.mediaDevices;
  if (!media?.getUserMedia) return;
  try {
    const stream = await media.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) track.stop();
  } catch {
    // Listening asks again and surfaces the failure.
  }
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

function startRingback(): { stop: () => void } {
  const Context =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Context) return { stop: () => undefined };
  let context: AudioContext;
  try {
    context = new Context();
  } catch {
    return { stop: () => undefined };
  }
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = RINGBACK_HZ;
  gain.gain.value = 0;
  oscillator.connect(gain);
  gain.connect(context.destination);
  const period = (TONE_MS + PAUSE_MS) / 1000;
  const start = context.currentTime + 0.05;
  // Scheduled ahead on the audio clock; a ring longer than this falls silent, not stuck.
  for (let index = 0; index < 12; index += 1) {
    gain.gain.setValueAtTime(TONE_GAIN, start + index * period);
    gain.gain.setValueAtTime(0, start + index * period + TONE_MS / 1000);
  }
  oscillator.start();
  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      try {
        oscillator.stop();
      } catch {
        // Already stopped.
      }
      void context.close().catch(() => undefined);
    },
  };
}
